"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { X } from 'lucide-react'
import Image from "next/image"

interface AddPetDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function AddPetDialog({ open, onOpenChange }: AddPetDialogProps) {
  const [animalType, setAnimalType] = useState<string>("")
  const [imagePreview, setImagePreview] = useState<string | null>(null)

  const animalTypes = [
    "Куче",
    "Котка",
    "Риба",
    "Птица",
    "Гризач",
    "Влечуго"
  ]

  const getDogBreeds = () => [
    { label: "Малки породи до 10 кг.", value: "small" },
    { label: "Средни породи от 11 до 25 кг.", value: "medium" },
    { label: "Големи породи от 26 до 45 кг.", value: "large" },
    { label: "Гигантски породи над 45 кг.", value: "giant" }
  ]

  const getCatBreeds = () => [
    { label: "Малки и средни до 20кг.", value: "small-medium" },
    { label: "Големи и гигантски над 20кг.", value: "large-giant" },
    { label: "S-M", value: "s-m" }
  ]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>Добавяне на любимец</DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 rounded-full"
              onClick={() => onOpenChange(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <form className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Тип животно</Label>
              <Select value={animalType} onValueChange={setAnimalType}>
                <SelectTrigger>
                  <SelectValue placeholder="Куче" />
                </SelectTrigger>
                <SelectContent>
                  {animalTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Порода</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Избери порода" />
                </SelectTrigger>
                <SelectContent>
                  {(animalType === "Куче" ? getDogBreeds() : getCatBreeds()).map((breed) => (
                    <SelectItem key={breed.value} value={breed.value}>
                      {breed.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Име</Label>
              <Input id="name" />
            </div>

            <div>
              <Label htmlFor="birthDate">Рождена дата</Label>
              <Input id="birthDate" type="date" />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded-full bg-gray-100 relative">
              {imagePreview && (
                <Image
                  src={imagePreview || "/placeholder.svg"}
                  alt="Pet preview"
                  fill
                  className="object-cover rounded-full"
                />
              )}
            </div>
            <div className="flex-1">
              <Label>Изображение</Label>
              <Button
                type="button"
                variant="secondary"
                className="ml-4"
                onClick={() => document.getElementById('image-upload')?.click()}
              >
                КАЧЕТЕ
              </Button>
              <input
                id="image-upload"
                type="file"
                className="hidden"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) {
                    const reader = new FileReader()
                    reader.onloadend = () => {
                      setImagePreview(reader.result as string)
                    }
                    reader.readAsDataURL(file)
                  }
                }}
              />
            </div>
          </div>

          <div>
            <Label>Пол</Label>
            <RadioGroup defaultValue="male" className="flex gap-4 mt-2">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="male" id="male" />
                <Label htmlFor="male">Мъж</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="female" id="female" />
                <Label htmlFor="female">Жена</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="allergies">Алергии</Label>
              <Input id="allergies" />
            </div>

            <div>
              <Label htmlFor="diseases">Заболявания</Label>
              <Input id="diseases" />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full bg-[#00A0E3] hover:bg-[#0090D3] text-white"
          >
            ДОБАВЯНЕ НА ЛЮБИМЕЦ
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}
